<template>
  <div class="calendar relative" @mousemove="handleMouseMove">
    <div class="flex items-center justify-between px-4 py-2 border-b">
      <h2 class="text-lg font-semibold">
        {{ currentMonthFormatted }}
      </h2>
      <div class="flex gap-2">
        <button @click="prev" class="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300">←</button>
        <button @click="next" class="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300">→</button>
      </div>
    </div>

    <div class="grid grid-cols-7 text-center font-medium text-sm py-2 border-b">
      <div v-for="day in weekDays" :key="day">{{ day }}</div>
    </div>

    <div class="grid grid-cols-7 grid-rows-6">
      <div
        v-for="(day, index) in days"
        :key="index"
        class="p-2 h-28 border text-xs text-gray-700 relative overflow-y-auto"
        :class="{ 'bg-gray-50 text-gray-400': day.getMonth() !== currentDate.getMonth() }"
      >
        <div class="absolute top-1 right-1 text-xs">
          {{ day.getDate() }}
        </div>
        <div
          v-for="event in getTrainingsForDay(day)"
          :key="event.id"
          class="mt-4 px-2 py-0.5 bg-indigo-100 border border-indigo-400 rounded text-indigo-800 text-xs cursor-pointer hover:bg-indigo-200 transition truncate"
          @mouseenter="!modalOpen && (hoveredEvent = event)"
          @mouseleave="!modalOpen && (hoveredEvent = null)"
          @click="openModal(event)"
        >
          {{ event.title }}
        </div>
      </div>
    </div>

    <div
      v-if="hoveredEvent"
      ref="tooltip"
      class="absolute z-50 px-4 py-2 bg-white border rounded-lg shadow-lg text-sm w-64 pointer-events-none"
      :style="tooltipStyle"
    >
      <div class="font-semibold mb-1">{{ hoveredEvent.title }}</div>
      <div class="text-gray-600 text-xs mb-1">
        {{ formatDateTime(hoveredEvent.date) }}
      </div>
      <div class="text-gray-700 text-sm">
        {{ hoveredEvent.description || 'Без описания' }}
      </div>
    </div>

    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div class="bg-white rounded-lg p-6 w-[90%] max-w-xl shadow-xl relative">
        <button
          class="absolute top-2 right-2 text-gray-500 hover:text-black text-xl"
          @click="closeModal"
        >
          &times;
        </button>

        <div class="text-lg font-semibold mb-2">
          {{ selectedEvent.title }}
        </div>

        <div class="text-sm text-gray-500 mb-2">
          {{ formatDateTime(selectedEvent.date) }}
        </div>

        <div class="text-sm mb-4 whitespace-pre-line">
          {{ selectedEvent.description || 'Без описания' }}
        </div>

        <div v-if="selectedEvent.instructor" class="text-sm mb-2">
          <strong>Инструктор:</strong>
          {{ selectedEvent.instructor }}
        </div>

        <div v-if="selectedEvent.link" class="text-sm">
          <strong>Ссылка: </strong>
          <a
            :href="selectedEvent.link"
            target="_blank"
            class="text-blue-600 underline break-all"
          >
            {{ selectedEvent.link }}
          </a>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { HEADERS_TOKEN_JSON, API_CLIENT } from '@/const/uri'

export default {
  name: 'CalendarTable',
  data() {
    return {
      currentDate: new Date(),
      trainings: [],
      hoveredEvent: null,
      mouseX: 0,
      mouseY: 0,
      showModal: false,
      modalOpen: false,
    }
  },
  computed: {
    currentYear() {
      return this.currentDate.getFullYear()
    },
    currentMonth() {
      return this.currentDate.getMonth() + 1
    },
    currentMonthFormatted() {
      return this.currentDate.toLocaleString('ru-RU', { month: 'long', year: 'numeric' })
    },
    weekDays() {
      return ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
    },
    startOfCalendar() {
      const date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1)
      const day = date.getDay() === 0 ? 7 : date.getDay()
      date.setDate(date.getDate() - (day - 1))
      return date
    },
    days() {
      const days = []
      const start = new Date(this.startOfCalendar)
      for (let i = 0; i < 42; i++) {
        days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i))
      }
      return days
    },
    tooltipStyle() {
      const tooltipWidth = 256
      const tooltipHeight = 100

      const rect = this.$el.getBoundingClientRect()

      let left = this.mouseX + 20
      let top = this.mouseY + 20

      if (left + tooltipWidth > rect.width) {
        left = this.mouseX - tooltipWidth - 20
      }

      if (top + tooltipHeight > rect.height) {
        top = this.mouseY - tooltipHeight - 20
      }

      return {
        left: `${left}px`,
        top: `${top}px`,
      }
    },
  },
  methods: {
    prev() {
      this.currentDate.setMonth(this.currentDate.getMonth() - 1)
      this.currentDate = new Date(this.currentDate)
      this.fetchTrainings()
    },
    next() {
      this.currentDate.setMonth(this.currentDate.getMonth() + 1)
      this.currentDate = new Date(this.currentDate)
      this.fetchTrainings()
    },
    goToday() {
      this.currentDate = new Date()
      this.fetchTrainings()
    },
    openModal(event) {
      this.modalOpen = true
      this.hoveredEvent = null
      this.selectedEvent = event
      this.showModal = true
    },
    getTrainingsForDay(date) {
      return this.trainings.filter(t => {
        const d = new Date(t.date)
        return d.toDateString() === date.toDateString()
      })
    },
    closeModal() {
      this.showModal = false;
    },
    formatDateTime(dateStr) {
      const d = new Date(dateStr)
      return d.toLocaleString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },
    handleMouseMove(e) {
      const containerRect = this.$el.getBoundingClientRect()
      const mouseX = e.clientX - containerRect.left
      const mouseY = e.clientY - containerRect.top

      this.mouseX = mouseX
      this.mouseY = mouseY

      this.$nextTick(() => {
        const tooltip = this.$refs.tooltip
        if (!tooltip) return

        const tooltipRect = tooltip.getBoundingClientRect()
        const tooltipWidth = tooltipRect.width
        const tooltipHeight = tooltipRect.height

        let left = mouseX + 20
        let top = mouseY + 20

        const availableRight = containerRect.width
        const availableBottom = containerRect.height
        // console.log(
        //   'availableRight:', availableRight, 
        //   'availableBottom:', availableBottom)

        if (left + tooltipWidth > availableRight) {
          left = mouseX - tooltipWidth - 20
        }
        if (top + tooltipHeight > availableBottom) {
          top = mouseY - tooltipHeight - 20
        }
        // console.log(
        //   'top:', top,)

        if (left < 0) left = 0
        if (top < 0) top = 0

        this.tooltipStyle = {
          left: `${left}px`,
          top: `${top}px`,
        }
      })
    },
    async fetchTrainings() {
      const year = this.currentYear
      const month = this.currentMonth

      await axios({
        method: 'GET',
        url: `${API_CLIENT.calendar}?year=${year}&month=${month}`,
        headers: HEADERS_TOKEN_JSON(),
      })
        .then((res) => {
          this.trainings = res.data.data
        })
        .catch((err) => {
          console.log(err)
        })
    },
  },
  mounted() {
    this.fetchTrainings()
  }
}
</script>

<style scoped>
.calendar {
  font-family: sans-serif;
}
</style>
